<template>
  <div class="guide-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <span class="section-eyebrow animate-on-scroll">GUIDE</span>
        <h1 class="animate-on-scroll aos-delay-1">使用指南</h1>
        <p class="animate-on-scroll aos-delay-2">快速上手 MookNote，开始记录你的生活</p>
      </div>
    </section>

    <!-- Quick Start -->
    <section class="quick-start">
      <div class="container">
        <span class="section-eyebrow animate-on-scroll" style="text-align:center">QUICK START</span>
        <h2 class="section-title animate-on-scroll">四步快速开始</h2>
        <p class="section-subtitle animate-on-scroll">无需注册，下载即可使用</p>
        <div class="steps">
          <div class="step animate-on-scroll">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>下载安装</h3>
              <p>从官网下载 Android APK、Windows 或 Linux 压缩包，安装后即可使用。</p>
            </div>
          </div>
          <div class="step animate-on-scroll aos-delay-1">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>添加记录</h3>
              <p>点击悬浮的「+」快捷入口，选择影视、阅读或游戏，填写信息保存；也可以用在线搜索自动填充资料。笔记则在「笔记」标签页中直接新建。</p>
            </div>
          </div>
          <div class="step animate-on-scroll aos-delay-2">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>查看管理</h3>
              <p>首页左右滑动切换四大模块，按状态筛选、多种排序与布局随心切换，点击卡片查看详情。</p>
            </div>
          </div>
          <div class="step animate-on-scroll aos-delay-3">
            <div class="step-number">4</div>
            <div class="step-content">
              <h3>备份同步</h3>
              <p>在「我的」页面配置本地备份或 WebDAV 云同步，定期备份，数据永不丢失。</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Detailed Guide -->
    <section class="detailed-guide">
      <div class="container">
        <span class="section-eyebrow animate-on-scroll" style="text-align:center">TUTORIAL</span>
        <h2 class="section-title animate-on-scroll">详细教程</h2>
        <p class="section-subtitle animate-on-scroll">按模块查看完整使用说明</p>
        <div class="guide-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="['tab-btn', { active: activeTab === tab.id }]"
            @click="activeTab = tab.id"
          >
            {{ tab.name }}
          </button>
        </div>

        <div class="guide-content">
          <!-- Movies Guide -->
          <div v-if="activeTab === 'movies'" class="guide-panel">
            <div class="guide-section">
              <h3>添加影视记录</h3>
              <ol>
                <li>点击底部悬浮的「+」按钮，选择「影视」类型</li>
                <li>填写名称（必填），选择分类：电影 / 电视剧 / 动漫 / 综艺 / 纪录片 / 短片</li>
                <li>选择观看状态：想看 / 在看 / 已看</li>
                <li>可选：添加海报、导演、演员、类型、评分与观看日期</li>
                <li>点击保存，完成建档</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>在线搜索添加</h3>
              <ol>
                <li>进入「在线搜索」页面，输入影视名称</li>
                <li>从搜索结果中选择匹配条目，支持分页浏览</li>
                <li>详情页可播放预告片，一键导入自动填充全部资料</li>
                <li>也可以通过豆瓣网页抓取，自动填充影视信息</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>影评与管理</h3>
              <ul>
                <li><strong>撰写影评：</strong>在详情页添加短评或长评，支持星级评分</li>
                <li><strong>海报墙：</strong>切换瀑布流布局浏览海报，支持多海报管理与全屏浏览</li>
                <li><strong>筛选排序：</strong>按状态筛选，支持更新时间 / 创建时间 / 评分排序</li>
                <li><strong>多种布局：</strong>海报网格 / 列表 / 大图卡片 / 年份网格自由切换</li>
                <li><strong>分享卡片：</strong>生成海报名场面风格的分享卡片</li>
              </ul>
            </div>
          </div>

          <!-- Reading Guide -->
          <div v-if="activeTab === 'reading'" class="guide-panel">
            <div class="guide-section">
              <h3>添加书籍</h3>
              <ol>
                <li>点击悬浮「+」按钮，选择「阅读」类型</li>
                <li>填写书名（必填），选择阅读状态：想读 / 在读 / 已读</li>
                <li>可选：添加封面、作者、出版社、ISBN、评分</li>
                <li>也可以通过在线搜索，从 300 万+ 书籍数据中快速建档</li>
                <li>点击保存</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>书摘与书评</h3>
              <ul>
                <li><strong>书摘记录：</strong>在详情页按章节添加摘录，支持撰写批注</li>
                <li><strong>书评撰写：</strong>支持短评与长评，记录阅读思考</li>
                <li><strong>分享卡片：</strong>将喜欢的书籍生成精美分享卡片</li>
              </ul>
            </div>
            <div class="guide-section">
              <h3>管理书籍</h3>
              <ul>
                <li><strong>筛选查看：</strong>按想读 / 在读 / 已读筛选</li>
                <li><strong>多种布局：</strong>海报网格 / 列表 / 大图卡片 / 年份网格</li>
                <li><strong>编辑删除：</strong>长按记录进行操作，删除后进入回收站</li>
              </ul>
            </div>
          </div>

          <!-- Games Guide -->
          <div v-if="activeTab === 'games'" class="guide-panel">
            <div class="guide-section">
              <h3>添加游戏</h3>
              <ol>
                <li>点击悬浮「+」按钮，选择「游戏」类型</li>
                <li>填写游戏名称（必填），选择分类：数字版 / 实体版 / 订阅制</li>
                <li>选择游玩状态：想玩 / 在玩 / 已通关 / 弃坑</li>
                <li>可选：添加封面、平台、版本、类型、购入价格与评分</li>
                <li>点击保存</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>游戏历程</h3>
              <ul>
                <li><strong>游玩时长：</strong>记录每款游戏投入的时间</li>
                <li><strong>游戏评测：</strong>撰写评测与心得，支持星级评分</li>
                <li><strong>截图画廊：</strong>上传游戏截图，在画廊中统一浏览</li>
                <li><strong>分享卡片：</strong>生成游戏分享卡片，晒出自己的收藏</li>
              </ul>
            </div>
          </div>

          <!-- Notes Guide -->
          <div v-if="activeTab === 'notes'" class="guide-panel">
            <div class="guide-section">
              <h3>添加笔记</h3>
              <ol>
                <li>切换到「笔记」标签页，点击「+」新建笔记</li>
                <li>输入标题与内容，支持 Markdown 语法</li>
                <li>编辑区与预览区实时渲染，所见即所得</li>
                <li>可选：添加标签进行分类</li>
                <li>点击保存</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>笔记管理</h3>
              <ul>
                <li><strong>多种布局：</strong>列表 / 瀑布流 / 时间线三种视图切换</li>
                <li><strong>笔记置顶：</strong>重要笔记一键置顶，始终排在最前</li>
                <li><strong>排序方式：</strong>支持更新时间 / 创建时间 / 标题排序</li>
                <li><strong>标签筛选：</strong>点击标签快速筛选同类笔记</li>
                <li><strong>全局搜索：</strong>跨影视、书籍、游戏、笔记统一搜索</li>
                <li><strong>分享导出：</strong>生成笔记分享卡片，图文并茂</li>
              </ul>
            </div>
          </div>

          <!-- Backup Guide -->
          <div v-if="activeTab === 'backup'" class="guide-panel">
            <div class="guide-section">
              <h3>本地备份</h3>
              <ol>
                <li>进入「我的」页面，点击「本地备份」</li>
                <li>选择「立即备份」，数据将打包为 ZIP 文件</li>
                <li>可开启定时自动备份，自动保留最近 5 份</li>
                <li>恢复时选择备份文件导入即可，支持 Excel 导出</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>WebDAV 云同步</h3>
              <p>将数据同步到支持 WebDAV 的网盘，如坚果云、Nextcloud 等。</p>
              <ol>
                <li>进入「我的」页面，点击「云备份」</li>
                <li>配置 WebDAV 服务器地址、用户名和密码</li>
                <li>点击「测试连接」验证配置后保存</li>
                <li>支持上传 / 下载 / 双向同步，可配置定时自动同步</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>回收站</h3>
              <ul>
                <li>删除的影视、书籍、游戏、笔记、影评、书评等会先进入回收站</li>
                <li>在「我的 - 回收站」中可以恢复或彻底删除</li>
                <li>建议重要操作前先进行一次本地备份</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="faq">
      <div class="container">
        <span class="section-eyebrow animate-on-scroll" style="text-align:center">FAQ</span>
        <h2 class="section-title animate-on-scroll">常见问题</h2>
        <p class="section-subtitle animate-on-scroll">遇到问题？先看看这里</p>
        <div class="faq-list">
          <div
            v-for="(item, index) in faqs"
            :key="index"
            :class="['faq-item', { active: activeFaq === index }]"
          >
            <div class="faq-question" @click="toggleFaq(index)">
              <span>{{ item.q }}</span>
              <span class="faq-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </span>
            </div>
            <div class="faq-answer" v-show="activeFaq === index">
              <p>{{ item.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Guide',
  data() {
    return {
      activeTab: 'movies',
      activeFaq: null,
      tabs: [
        { id: 'movies', name: '影视记录' },
        { id: 'reading', name: '阅读管理' },
        { id: 'games', name: '游戏收藏' },
        { id: 'notes', name: '笔记功能' },
        { id: 'backup', name: '备份同步' }
      ],
      faqs: [
        // {
        //   q: 'MookNote 是免费的吗？',
        //   a: '是的，MookNote 本地功能完全免费，不涉及任何收费项，只有用到云端功能的时候才会收费，作者仅仅希望能覆盖一下电费就好了。'
        // },
        {
          q: '支持哪些平台？',
          a: '目前完整支持 Android（5.0 及以上）、Windows 与 Linux 桌面端（宽屏主从布局适配），iOS 版本正在规划中。'
        },
        {
          q: '数据存储在哪里？',
          a: '所有数据都存储在您的设备本地（SQLite 数据库），无需注册账号，我们不会上传任何数据，隐私完全由你掌控。'
        },
        {
          q: '如何迁移数据到新设备？',
          a: '使用本地备份功能导出 ZIP 文件，在新设备上导入即可；也可以配置 WebDAV 云同步，在多设备间自动同步数据。'
        },
        {
          q: '删除的记录可以恢复吗？',
          a: '可以。删除的内容会先进入回收站，在「我的 - 回收站」中可以恢复。彻底删除后将无法找回，请谨慎操作。'
        },
        {
          q: '笔记支持什么格式？',
          a: '笔记支持 Markdown 语法编辑，编辑与预览实时渲染，支持置顶、标签分类，以及列表 / 瀑布流 / 时间线三种浏览布局。'
        },
        {
          q: '在线搜索的数据量有多大？',
          a: '已对接 6 万+ 影视基础数据与 300 万+ 书籍基础信息，搜索即可一键建档，无需手动填写资料。'
        }
      ]
    }
  },
  mounted() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        }
      })
    }, { threshold: 0.1 })

    this.$nextTick(() => {
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el))
    })
  },
  methods: {
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index
    }
  }
}
</script>

<style scoped>
/* ===== Page Header ===== */
.page-header {
  padding: 170px 24px 90px;
  background:
    radial-gradient(640px 360px at 50% 0%, rgba(0, 102, 255, 0.05), transparent 70%),
    linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 700;
  letter-spacing: -1px;
  margin-bottom: 16px;
  color: var(--color-text);
}

.page-header p {
  font-size: 16.5px;
  color: var(--color-text-secondary);
}

/* ===== Quick Start ===== */
.quick-start {
  padding: 100px 24px;
  background: #fff;
}

.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 20px;
}

.step {
  padding: 30px 26px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: transform 0.35s var(--ease-out), box-shadow 0.35s var(--ease-out), background 0.35s;
}

.step:hover {
  transform: translateY(-5px);
  background: #fff;
  box-shadow: var(--shadow-lg);
}

.step-number {
  width: 40px;
  height: 40px;
  background: var(--color-text);
  color: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 20px;
  font-variant-numeric: tabular-nums;
  transition: transform 0.3s var(--ease-out);
}

.step:hover .step-number {
  transform: scale(1.08) rotate(-4deg);
}

.step-content h3 {
  font-size: 17.5px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--color-text);
}

.step-content p {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.8;
}

/* ===== Detailed Guide ===== */
.detailed-guide {
  padding: 100px 24px;
  background: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
}

.guide-tabs {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 36px;
}

.tab-btn {
  padding: 11px 24px;
  border: 1px solid var(--color-border);
  background: #fff;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  font-family: var(--font-family);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.25s var(--ease-out);
}

.tab-btn:hover {
  border-color: var(--color-text-muted);
  color: var(--color-text);
  transform: translateY(-1px);
}

.tab-btn.active {
  background: var(--color-text);
  border-color: var(--color-text);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(16, 16, 20, 0.35);
}

.guide-content {
  max-width: 820px;
  margin: 0 auto;
}

.guide-panel {
  background: #fff;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 44px;
  animation: panelIn 0.4s var(--ease-out);
}

@keyframes panelIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.guide-section {
  margin-bottom: 36px;
}

.guide-section:last-child {
  margin-bottom: 0;
}

.guide-section h3 {
  font-size: 19px;
  font-weight: 600;
  margin-bottom: 18px;
  color: var(--color-text);
  padding-bottom: 14px;
  border-bottom: 1px solid var(--color-border);
}

.guide-section ol,
.guide-section ul {
  padding-left: 22px;
  color: var(--color-text-secondary);
  line-height: 2.05;
  font-size: 14.5px;
}

.guide-section li {
  margin-bottom: 6px;
}

.guide-section li::marker {
  color: var(--color-text-muted);
}

.guide-section strong {
  color: var(--color-text);
  font-weight: 600;
}

.guide-section p {
  color: var(--color-text-secondary);
  line-height: 1.9;
  font-size: 14.5px;
  margin-bottom: 14px;
}

/* ===== FAQ ===== */
.faq {
  padding: 100px 24px 110px;
  background: #fff;
}

.faq-list {
  max-width: 820px;
  margin: 0 auto;
}

.faq-item {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: 12px;
  background: #fff;
  transition: border-color 0.25s, box-shadow 0.25s;
  overflow: hidden;
}

.faq-item:hover {
  border-color: var(--color-border-strong);
}

.faq-item.active {
  border-color: var(--color-border-strong);
  box-shadow: var(--shadow-md);
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  cursor: pointer;
  font-size: 15.5px;
  font-weight: 600;
  color: var(--color-text);
  user-select: none;
}

.faq-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  flex-shrink: 0;
  transition: transform 0.3s var(--ease-out), background 0.3s;
}

.faq-item.active .faq-icon {
  transform: rotate(180deg);
  background: var(--color-text);
  color: #fff;
}

.faq-answer {
  padding: 0 24px 22px;
  animation: panelIn 0.3s var(--ease-out);
}

.faq-answer p {
  color: var(--color-text-secondary);
  line-height: 1.9;
  font-size: 14.5px;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .page-header {
    padding-top: 150px;
  }

  .guide-tabs {
    gap: 6px;
  }

  .tab-btn {
    padding: 9px 18px;
    font-size: 13.5px;
  }

  .guide-panel {
    padding: 26px 20px;
  }
}
</style>
